import classes from "./TeamGrade.module.css"
import "@fontsource/roboto"
import InputBlock from "components/UI/inputBlock/InputBlock"
import { useState, useRef, useEffect, useCallback } from 'react'
import { Card } from "./Card"
import Button from "components/UI/buttons/button/Button"
import OccasionService from "services/OccasionService"
import TitleMain from "components/UI/titleMain/TitleMain";

const normalizeNumber = (value) => {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : 0
}

const formatNumber = (value, digits = 2) => normalizeNumber(value).toFixed(digits)

const buildImageUrl = (imageSrc) => {
    if (!imageSrc || typeof imageSrc !== 'string' || imageSrc === 'EDRO_CONTENT/SurGU_Events/assets/images/upload.png') {
        return null
    }
    return imageSrc
}

const getInitials = (text = '') => {
    const words = text.trim().split(/\s+/).filter(Boolean)
    if (!words.length) return '??'
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
    return (words[0][0] + words[1][0]).toUpperCase()
}

const aggregateMentorCoefficients = (checkPoints = []) => {
    const aggregated = {}
    checkPoints.forEach(item => {
        const commandId = item?.command_id
        if (!commandId) return
        const coefficient = normalizeNumber(item?.coefficient)
        if (!Number.isFinite(coefficient)) return
        if (!aggregated[commandId]) {
            aggregated[commandId] = { sum: 0, count: 0 }
        }
        aggregated[commandId].sum += coefficient
        aggregated[commandId].count += 1
    })
    return Object.entries(aggregated).reduce((acc, [commandId, { sum, count }]) => {
        acc[commandId] = count > 0 ? sum / count : 0
        return acc
    }, {})
}

const aggregateCodeCoefficients = (data) => {
    const map = {}
    if (Array.isArray(data?.codeReview)) {
        data.codeReview.forEach(item => {
            const commandId = item?.command_id ?? item?.command
            if (!commandId) return
            const coefficient = normalizeNumber(item?.coefficient ?? item?.grade_coefficient ?? item?.grade ?? 0)
            map[commandId] = coefficient
        })
    }
    if (Array.isArray(data?.finalGrade)) {
        data.finalGrade.forEach(item => {
            const commandId = item?.command_id
            if (!commandId) return
            const coefficient = normalizeNumber(
                item?.code_review_coefficient ??
                item?.code_coefficient ??
                item?.codeCoefficient ??
                item?.code_grade ??
                item?.code_review ??
                0
            )
            if (coefficient) {
                map[commandId] = coefficient
            }
        })
    }
    return map
}

const aggregateMentorRatings = (checkPoints = []) => {
    const aggregated = {}
    checkPoints.forEach(item => {
        const commandId = item?.command_id
        if (!commandId) return
        const juryRatings = Array.isArray(item?.jury_ratings) ? item.jury_ratings : []
        if (!aggregated[commandId]) {
            aggregated[commandId] = []
        }
        // Собираем все оценки менторов из всех чекпоинтов
        juryRatings.forEach(rating => {
            aggregated[commandId].push({
                jury_id: rating?.jury_id,
                jury_name: rating?.jury_name || 'Ментор',
                grade_sum: normalizeNumber(rating?.grade_sum),
                coefficient: normalizeNumber(rating?.coefficient),
                num_check: item?.num_check
            })
        })
    })
    // Группируем по менторам и суммируем их оценки
    const result = {}
    Object.entries(aggregated).forEach(([commandId, ratings]) => {
        const mentorMap = {}
        ratings.forEach(rating => {
            const mentorId = rating.jury_id
            if (!mentorMap[mentorId]) {
                mentorMap[mentorId] = {
                    jury_id: mentorId,
                    jury_name: rating.jury_name,
                    grade_sum: 0,
                    coefficient: 0,
                    count: 0
                }
            }
            mentorMap[mentorId].grade_sum += rating.grade_sum
            mentorMap[mentorId].coefficient += rating.coefficient
            mentorMap[mentorId].count += 1
        })
        // Вычисляем средние значения
        result[commandId] = Object.values(mentorMap).map(mentor => ({
            ...mentor,
            grade_sum: mentor.grade_sum,
            coefficient: mentor.count > 0 ? mentor.coefficient / mentor.count : 0
        }))
    })
    return result
}

const TeamGrade = ({ onClick,
    occasions,
    occasionList,
    resultList,
    fetchingRatings, updateRatings, notificationError,
    currentOccasionId, onCurrentOccasionChange, onRefreshRatings, onRefreshRatingsFast, loadingFetchingRatings, loadingFetchingRatingsFast }) => {
    // const [events, setOccasions] = useState()
    const [currentOccasion, setCurrentOccasion] = useState({})
    const ref = useRef()

    const [result, setResult] = useState([])
    const [juryResult, setJuryResult] = useState([])
    const [massCategory, setMassCategory] = useState([])
    const [finalSections, setFinalSections] = useState([])
    const [imageBlobUrls, setImageBlobUrls] = useState({}) // Хранилище blob URL для изображений команд
    const [fullscreenImage, setFullscreenImage] = useState(null) // Открытое изображение на весь экран
    const imageBlobUrlsRef = useRef({}) // Ref для хранения актуальных blob URLs для очистки
    const [showScrollTop, setShowScrollTop] = useState(false) // Показывать ли кнопку "наверх"
    const placesRef = useRef(null) // Ref для блока "Места команд"

    useEffect(() => {
        if (currentOccasionId && occasions.length > 0) {
            const occasion = occasions.find(occ => occ.id === currentOccasionId)
            if (occasion) {
                setCurrentOccasion(occasion)
            }
        }
    }, [currentOccasionId, occasions])

    // Обработка изображений команд: преобразование Yandex.Disk URL в blob URL (по аналогии с Profile.jsx)
    useEffect(() => {
        if (resultList["finalGrade"] && Array.isArray(resultList["finalGrade"])) {
            const newBlobUrls = {}
            const processedCommands = new Set()
            
            resultList["finalGrade"].forEach(item => {
                const imageCommand = item?.image_command
                const commandId = item?.command_id
                
                if (imageCommand && commandId && imageCommand !== 'EDRO_CONTENT/SurGU_Events/assets/images/upload.png') {
                    processedCommands.add(commandId)
                    
                    // Если уже есть blob URL для этой команды, сохраняем его
                    if (imageBlobUrls[commandId]) {
                        newBlobUrls[commandId] = imageBlobUrls[commandId]
                    } else {
                        // делаем запрос в яндекс и получаем данные об файле
                        fetch(`https://cloud-api.yandex.net/v1/disk/public/resources?public_key=${imageCommand}`)
                            .then(responseApi => responseApi.json())
                            // убираем из url файла данные об его размере
                            .then(data => data.preview?.split("size")[0])
                            // добавляем в url файла свои данные об размере
                            .then(responseUrl => `${responseUrl}size=512x512`)
                            // делаем запрос для получение изображения и ждем пока все пакеты придут
                            .then(newUrl => fetch(newUrl))
                            .then(response => response.blob())
                            // формируем локальный url для фотографии и добавляем его в состояние
                            .then(image => {
                                const blobUrl = URL.createObjectURL(image)
                                setImageBlobUrls(prev => {
                                    const updated = {
                                        ...prev,
                                        [commandId]: blobUrl
                                    }
                                    imageBlobUrlsRef.current = updated
                                    return updated
                                })
                            })
                            .catch(error => {
                                console.error('Error processing image for command', commandId, error)
                            })
                    }
                }
            })
            
            // Очищаем blob URLs для команд, которых больше нет в данных
            Object.keys(imageBlobUrls).forEach(commandId => {
                if (!processedCommands.has(Number(commandId))) {
                    const url = imageBlobUrls[commandId]
                    if (url && typeof url === 'string' && url.startsWith('blob:')) {
                        URL.revokeObjectURL(url)
                    }
                }
            })
        }

        // Очистка blob URL при размонтировании компонента
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultList])

    // Обновляем ref при изменении imageBlobUrls
    useEffect(() => {
        imageBlobUrlsRef.current = imageBlobUrls
    }, [imageBlobUrls])

    // Очистка всех blob URLs при размонтировании компонента
    useEffect(() => {
        return () => {
            Object.values(imageBlobUrlsRef.current).forEach(url => {
                if (url && typeof url === 'string' && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url)
                }
            })
        }
    }, [])

    // Отслеживание скролла для кнопки "наверх"
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop
            setShowScrollTop(scrollTop > 100)
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    // Функция для скролла к блоку "Места команд"
    const scrollToPlaces = () => {
        if (placesRef.current) {
            placesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }

    // Функция для скролла наверх
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    useEffect(() => {
        setResult([])
        setJuryResult([])
        setMassCategory([])
        setFinalSections([])

        if (Object.keys(resultList).length > 0 && resultList["finalGrade"] && Array.isArray(resultList["finalGrade"]) && resultList["finalGrade"].length > 0) {
            const sortedFinalGrade = [...resultList["finalGrade"]].sort((a, b) => {
                const aCoeff = normalizeNumber(a?.total_coefficient ?? 0)
                const bCoeff = normalizeNumber(b?.total_coefficient ?? 0)
                return bCoeff - aCoeff
            })
            const names = sortedFinalGrade.map((values) => values["name"])
            const unique = [...new Set(names)]
            
            const newResult = []
            if (resultList["checkPoint"] && Array.isArray(resultList["checkPoint"]) && resultList["checkPoint"].length > 0 && resultList["checkPoint"][0] && resultList["checkPoint"][0]["check_point_count"]) {
                const checkPointCount = resultList["checkPoint"][0]["check_point_count"]
                unique.forEach((valuesC, j) => {
                    newResult.push(
                        <div key={j} className={classes.checkpoint__container}>
                            {Array.from({ length: checkPointCount }, (_, i) => (
                                <div className={classes.checkpoint__block} key={i}>
                                    <span>Check point №{i + 1} {valuesC}</span>
                                    <div className={classes.border__for__list}>
                                        {resultList["checkPoint"].map((values, index) => (
                                            values && values["category_name"] === valuesC && values["num_check"] === i + 1 && (
                                                <span key={index}>
                                                    {values["table_number"]} : {values["name"]} - {values["total_grade"]}
                                                </span>
                                            ))
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                });
            }
            setResult(newResult)

            // Блок для оценок жюри (финальных оценок)
            const newJuryResult = []
            if (resultList["finalGrade"] && Array.isArray(resultList["finalGrade"]) && resultList["finalGrade"].length > 0) {
                unique.forEach((valuesC, j) => {
                    const categoryCommands = sortedFinalGrade
                        .filter(item => item && item.name === valuesC)
                        .sort((a, b) => {
                            const aGrade = normalizeNumber(a?.total_grade ?? 0)
                            const bGrade = normalizeNumber(b?.total_grade ?? 0)
                            return bGrade - aGrade
                        })
                    newJuryResult.push(
                        <div key={j} className={classes.checkpoint__container}>
                            <div className={classes.checkpoint__block}>
                                <span>Оценки жюри {valuesC}</span>
                                <div className={classes.border__for__list}>
                                    {categoryCommands.map((command, index) => (
                                        command && (
                                            <span key={index}>
                                                {command["table_number"]} : {command["command_name"]} - {formatNumber(command["total_grade"])}
                                            </span>
                                        )
                                    ))}
                                </div>
                            </div>
                        </div>
                    )
                });
            }
            setJuryResult(newJuryResult)

            setMassCategory(unique?.map(item => (
                {
                    [item]: resultList["finalGrade"].filter(obj => obj && obj.name === item).map((values, index) => (values && values["name"] === item) && ({ id: values["command_id"], text: values["command_name"] })).filter(Boolean)
                }
            )).filter(item => item && Object.keys(item)[0] && Object.values(item)[0] && Object.values(item)[0].length > 0))

            const mentorCoefficients = aggregateMentorCoefficients(resultList?.checkPoint)
            const codeCoefficients = aggregateCodeCoefficients(resultList)
            const mentorRatings = aggregateMentorRatings(resultList?.checkPoint)

            const sections = unique.map(categoryName => {
                const categoryCommands = sortedFinalGrade
                    .filter(item => item && item.name === categoryName)
                    .sort((a, b) => {
                        const aCoeff = normalizeNumber(a?.total_coefficient ?? 0)
                        const bCoeff = normalizeNumber(b?.total_coefficient ?? 0)
                        return bCoeff - aCoeff
                    })
                return {
                    category: categoryName,
                    commands: categoryCommands.map((item, indexInCategory) => {
                        const commandId = item?.command_id
                        const juryCoefficient = normalizeNumber(item?.coefficient)
                        // Используем checkpoint_coefficient из данных, если есть, иначе вычисляем
                        const mentorCoefficient = normalizeNumber(
                            item?.checkpoint_coefficient ?? 
                            mentorCoefficients?.[commandId] ?? 
                            0
                        )
                        // Используем code_review_coefficient из данных, если есть, иначе вычисляем
                        const codeCoefficient = normalizeNumber(
                            item?.code_review_coefficient ?? 
                            item?.code_coefficient ?? 
                            item?.code_grade ??
                            codeCoefficients?.[commandId] ?? 
                            0
                        )
                        const totalGradeValue = normalizeNumber(item?.total_grade)
                        const avgGradeValue = normalizeNumber(item?.avg_grade)
                        
                        // Данные жюри
                        const juryRatings = Array.isArray(item?.jury_ratings) ? item.jury_ratings : []
                        const juryTotalSum = juryRatings.reduce((sum, rating) => sum + normalizeNumber(rating?.grade_sum), 0)
                        const juryAvgGrade = juryRatings.length > 0 ? juryTotalSum / juryRatings.length : 0
                        
                        // Данные менторов
                        const mentorRatingsList = mentorRatings[commandId] || []
                        const mentorTotalSum = mentorRatingsList.reduce((sum, mentor) => sum + normalizeNumber(mentor?.grade_sum), 0)
                        const mentorAvgGrade = mentorRatingsList.length > 0 ? mentorTotalSum / mentorRatingsList.length : 0

                        // Используем blob URL если есть, иначе исходный URL
                        const imageSrc = imageBlobUrls[commandId] || buildImageUrl(item?.image_command)

                        // Используем total_coefficient из данных, если есть, иначе вычисляем как среднее
                        const totalCoefficient = normalizeNumber(
                            item?.total_coefficient ?? 
                            (juryCoefficient + mentorCoefficient + codeCoefficient) / 3.0
                        )

                        return {
                            ...item,
                            participants: Array.isArray(item?.participants) ? item.participants : [],
                            total_grade_value: totalGradeValue,
                            avg_grade_value: avgGradeValue,
                            juryCoefficient,
                            mentorCoefficient,
                            codeCoefficient,
                            totalCoefficient,
                            imageSrc,
                            juryRatings,
                            juryTotalSum,
                            juryAvgGrade,
                            mentorRatings: mentorRatingsList,
                            mentorTotalSum,
                            mentorAvgGrade,
                            max_possible_grade: normalizeNumber(item?.max_possible_grade),
                            checkpoint_max_grade: normalizeNumber(resultList?.checkPoint?.[0]?.max_possible_grade),
                            rank: indexInCategory + 1
                        }
                    })
                }
            })

            setFinalSections(sections)

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultList, imageBlobUrls])


    function changeOcassion(data, index) {
        const occasion = occasions[index]
        setCurrentOccasion(occasion)
        setResult([])
        if (onCurrentOccasionChange) {
            onCurrentOccasionChange(occasion.id)
        }
        fetchingRatings(occasion.id)
    }


    const moveCard = useCallback((categoryKey, dragIndex, hoverIndex) => {
        setMassCategory((prevMassCategory) => {
            if (!Array.isArray(prevMassCategory) || prevMassCategory.length === 0) {
                return prevMassCategory
            }
            // ищем из массива тот элемент в котором есть нужная нам категория
            const prevCategoryElement = prevMassCategory.find(item => item && Object.keys(item)[0] === categoryKey)
            if (!prevCategoryElement || !prevCategoryElement[categoryKey]) {
                return prevMassCategory
            }
            // получаем ту категорию, в которой были изменения
            const categoryArray = prevCategoryElement[categoryKey]
            if (!Array.isArray(categoryArray) || dragIndex < 0 || hoverIndex < 0 || dragIndex >= categoryArray.length || hoverIndex >= categoryArray.length) {
                return prevMassCategory
            }
            let newCategoryArray = [...categoryArray];
            // меняем элементы массива местами (у конкретной категории)
            [newCategoryArray[dragIndex], newCategoryArray[hoverIndex]] = [newCategoryArray[hoverIndex], newCategoryArray[dragIndex]];
            // ищем из массива тот индекс того элемент в котором есть нужная нам категория
            const changedElementIndex = prevMassCategory.findIndex(item => item && Object.keys(item)[0] === categoryKey)
            if (changedElementIndex === -1) {
                return prevMassCategory
            }
            // обновляем по категорию
            const updatedMassCategory = [...prevMassCategory]
            updatedMassCategory[changedElementIndex] = {
                ...updatedMassCategory[changedElementIndex],
                [categoryKey]: newCategoryArray
            }
            return updatedMassCategory
        });
    }, []);


    return (
        <div ref={ref} className={classes.main__block__style}>
            <TitleMain text={"Оценка команд"} jsc="center" fns="1.5vw" elpSize="0.9vw" />
            {/* <h3>Оценка команд</h3> */}
            <div className={classes.headerSection}>
                <div className={classes.occasionSelector}>
                    <InputBlock 
                        indexStatus={true} 
                        readOnly={true} 
                        placeholder={"Мероприятие"} 
                        onListStatus={true} 
                        list={occasionList} 
                        value={OccasionService.getTextInputOccasion(currentOccasion)} 
                        setValue={changeOcassion} 
                        refMainContainer={ref}
                    >
                        <img src={require("assets/images/arrow.jpg")} alt="" />
                    </InputBlock>
                </div>
                {Object.values(currentOccasion).length > 0 && (
                    <div className={classes.refreshButtons}>
                        {onRefreshRatingsFast && (
                            <Button 
                                onClick={onRefreshRatingsFast} 
                                disabled={loadingFetchingRatingsFast || loadingFetchingRatings}
                                className={classes.refreshButton}
                            >
                                {loadingFetchingRatingsFast ? "Обновление..." : "Быстрое обновление"}
                            </Button>
                        )}
                        {onRefreshRatings && (
                            <Button 
                                onClick={onRefreshRatings} 
                                disabled={loadingFetchingRatings || loadingFetchingRatingsFast}
                                className={classes.refreshButton}
                            >
                                {loadingFetchingRatings ? "Обновление..." : "Полное обновление"}
                            </Button>
                        )}
                        {massCategory.length > 0 && (
                            <Button 
                                onClick={scrollToPlaces}
                                className={classes.anchorButton}
                            >
                                К местам команд
                            </Button>
                        )}
                    </div>
                )}
            </div>
            {/* --------------------------Финалые баллы----------------------- */}
            {
                finalSections.length > 0 && finalSections.map(({ category, commands }) => (
                    <div key={category} className={classes.finalSection}>
                        <div className={classes.finalSectionHeader}>
                            <span className={classes.finalSectionTitle}>Финальная оценка категории {category}</span>
                            <span className={classes.finalSectionHint}>Кейс, участники, сумма и средний балл, а также подробный расчет коэффициентов</span>
                        </div>
                        <div className={classes.finalCards}>
                            {commands.map(command => (
                                <div key={`${command.command_id}-${command.table_number}`} className={classes.finalCard}>
                                    <div className={classes.finalCardHeader}>
                                        <div className={classes.commandIdentity}>
                                            <span className={classes.commandName}>{command.command_name}</span>
                                            <span className={classes.commandTable}>Стол №{command.table_number || '—'}</span>
                                        </div>
                                        <div className={classes.commandRankBadge}>Место {command.rank}</div>
                                    </div>

                                    {command.task_title && (
                                        <div className={classes.caseRow}>
                                            <span className={classes.caseLabel}>Кейс:</span>
                                            <span className={classes.caseValue}>{command.task_title}</span>
                                        </div>
                                    )}

                                    <div className={classes.identitySection}>
                                        <div 
                                            className={classes.finalCardImage}
                                            onClick={() => command.imageSrc && setFullscreenImage(command.imageSrc)}
                                            style={command.imageSrc ? { cursor: 'pointer' } : {}}
                                        >
                                            {command.imageSrc
                                                ? (
                                                    <img
                                                        src={command.imageSrc}
                                                        alt={`Команда ${command.command_name}`}
                                                        loading="lazy"
                                                    />
                                                )
                                                : (
                                                    <div className={classes.finalCardImagePlaceholder}>
                                                        {getInitials(command.command_name)}
                                                    </div>
                                                )}
                                        </div>
                                        <div className={classes.infoBlocksRow}>
                                            <div className={classes.participantsBlock}>
                                                <span className={classes.caseLabel}>Участники:</span>
                                                {command.participants?.length > 0 ? (
                                                    <ul>
                                                        {command.participants.map((participant) => (
                                                            participant?.full_name ? (
                                                                <li key={participant.account_id}>{participant.full_name}</li>
                                                            ) : null
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <span className={classes.participantsEmpty}>Нет данных</span>
                                                )}
                                            </div>

                                            {/* Блок Жюри */}
                                            <div className={classes.evaluationBlock}>
                                                <div className={classes.evaluationBlockTitle}>Жюри</div>
                                                <div className={classes.evaluationBlockContent}>
                                                    {Array.isArray(command.juryRatings) && command.juryRatings.length > 0 ? (
                                                        <>
                                                            <div className={classes.expertsList}>
                                                                {command.juryRatings.map((rating, idx) => (
                                                                    <div key={rating?.jury_id || idx} className={classes.expertItem}>
                                                                        <span className={classes.expertName}>
                                                                            Жюри {idx + 1}:
                                                                        </span>
                                                                        <strong className={classes.expertGrade}>
                                                                            {formatNumber(rating?.grade_sum)}
                                                                        </strong>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className={classes.evaluationSummary}>
                                                                <div className={classes.summaryRow}>
                                                                    <span>Сумма:</span>
                                                                    <strong>{formatNumber(command.juryTotalSum)}</strong>
                                                                </div>
                                                                <div className={classes.summaryRow}>
                                                                    <span>Средний:</span>
                                                                    <strong>{formatNumber(command.juryAvgGrade)}</strong>
                                                                </div>
                                                                <div className={classes.summaryRow}>
                                                                    <span>Коэфф.:</span>
                                                                    <strong>{formatNumber(command.juryCoefficient)}</strong>
                                                                </div>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className={classes.noData}>Нет данных</div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Блок Менторы */}
                                            <div className={classes.evaluationBlock}>
                                                <div className={classes.evaluationBlockTitle}>Менторы</div>
                                                <div className={classes.evaluationBlockContent}>
                                                    {Array.isArray(command.mentorRatings) && command.mentorRatings.length > 0 ? (
                                                        <>
                                                            <div className={classes.expertsList}>
                                                                {command.mentorRatings.map((mentor, idx) => (
                                                                    <div key={mentor?.jury_id || idx} className={classes.expertItem}>
                                                                        <span className={classes.expertName}>
                                                                            Ментор {idx + 1}:
                                                                        </span>
                                                                        <strong className={classes.expertGrade}>
                                                                            {formatNumber(mentor?.grade_sum)}
                                                                        </strong>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className={classes.evaluationSummary}>
                                                                <div className={classes.summaryRow}>
                                                                    <span>Сумма:</span>
                                                                    <strong>{formatNumber(command.mentorTotalSum)}</strong>
                                                                </div>
                                                                <div className={classes.summaryRow}>
                                                                    <span>Средний:</span>
                                                                    <strong>{formatNumber(command.mentorAvgGrade)}</strong>
                                                                </div>
                                                                <div className={classes.summaryRow}>
                                                                    <span>Коэфф.:</span>
                                                                    <strong>{formatNumber(command.mentorCoefficient)}</strong>
                                                                </div>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className={classes.noData}>Нет данных</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Коэффициент кода */}
                                    <div className={classes.codeCoefficientBlock}>
                                        <span>Коэффициент кода:</span>
                                        <strong>{formatNumber(command.codeCoefficient)}</strong>
                                    </div>

                                    <div className={classes.totalCoefficient}>
                                        Среднее (менторы + жюри + код) / 3 = <strong>{formatNumber(command.totalCoefficient)}</strong>
                                    </div>
                                    <div className={classes.placeBadge}>
                                        Место {command.rank}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))
            }
            {/* ------------------------Места команд--------------------------- */}
            {Object.values(currentOccasion).length > 0 && massCategory.length > 0 && <div ref={placesRef} className={classes.grade__team__style} id="places-section">
                {massCategory.map((valuesC, indexC) => {
                    const categoryKey = Object.keys(valuesC)[0]
                    const categoryValues = Object.values(valuesC)[0]
                    if (!categoryKey || !Array.isArray(categoryValues) || categoryValues.length === 0) {
                        return null
                    }
                    return (
                        <div key={indexC} className={classes.list__comand__style}>
                            <span className={classes.span__style}>Места команд {categoryKey}</span>
                            {categoryValues.map((card, i) => (
                                card && (
                                    <Card
                                        key={i}
                                        index={i}
                                        id={card.id}
                                        text={card.text}
                                        moveCard={(dragIndex, hoverIndex) => moveCard(categoryKey, dragIndex, hoverIndex)}
                                    />
                                )
                            ))}
                            <Button onClick={() => updateRatings({ data: massCategory, category: categoryKey })}>Сохранить</Button>
                        </div>
                    )
                })}
            </div>}
            {/* --------------------------Баллы по категориям----------------------- */}
            {
                Object.values(currentOccasion).length > 0 &&
                <div className={classes.check__point__block}>
                    {result && result}
                </div>
            }
            {/* --------------------------Оценки жюри----------------------- */}
            {
                Object.values(currentOccasion).length > 0 &&
                <div className={classes.check__point__block}>
                    {juryResult && juryResult}
                </div>
            }
            {/* Кнопка "наверх" */}
            {showScrollTop && (
                <button 
                    onClick={scrollToTop}
                    className={classes.scrollToTopButton}
                    aria-label="Наверх"
                >
                    ↑
                </button>
            )}
            {/* Модальное окно для полноэкранного просмотра изображения */}
            {fullscreenImage && (
                <div 
                    className={classes.imageModalOverlay}
                    onClick={() => setFullscreenImage(null)}
                >
                    <div 
                        className={classes.imageModalContent}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img 
                            src={fullscreenImage} 
                            alt="Полноэкранное изображение команды"
                            className={classes.imageModalImage}
                        />
                    </div>
                </div>
            )}
        </div >
    )
}
export default TeamGrade